import { useRef, useEffect, useCallback, useState } from "react";
import * as THREE from "three";
import type { HarmonicAnalysis } from "@/lib/resonance-audio";

export interface ManifoldPoint {
  messageId: string;
  index: number;
  analysis: HarmonicAnalysis;
}

interface ManifoldNode {
  position: THREE.Vector3;
  color: THREE.Color;
  radius: number;
  age: number;
  messageId: string;
  index: number;
  analysis: HarmonicAnalysis;
  mesh: THREE.Mesh | null;
}

interface ManifoldEdge {
  from: number;
  to: number;
  weight: number;
  line: THREE.Line | null;
}

interface SelectedPoint {
  messageId: string;
  index: number;
  x: number;
  y: number;
  z: number;
  key: string;
  scaleDegree: number;
  mode: string;
  centroid: number;
  density: number;
  consonanceRatio: number;
  mappedColor: string;
  baseFrequency: number;
}

export interface HarmonicManifoldProps {
  points: ManifoldPoint[];
  onPointClick?: (messageId: string) => void;
  className?: string;
  width?: number;
  height?: number;
}

const BG_COLOR = new THREE.Color(0.015, 0.015, 0.04);
const GRID_COLOR = new THREE.Color(0.06, 0.06, 0.12);
const MAX_NODES = 64;
const TIME_DECAY_RATE = 0.003;
const MIN_OPACITY = 0.15;
const NODE_BASE_RADIUS = 0.06;
const EDGE_OPACITY_BASE = 0.4;
const CAMERA_DISTANCE = 4;
const CAMERA_ORBIT_SPEED = 0.08;
const HIGHLIGHT_EMISSIVE = 0.9;
const MOBILE_MAX_TRIANGLES = 12;
const DESKTOP_MAX_TRIANGLES = 16;

function isMobile(): boolean {
  return window.innerWidth < 768 || "ontouchstart" in window;
}

function parseHSL(hslStr: string): THREE.Color {
  const match = hslStr.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) return new THREE.Color(0.5, 0.5, 0.5);
  const h = parseInt(match[1]) / 360;
  const s = parseInt(match[2]) / 100;
  const l = parseInt(match[3]) / 100;
  return new THREE.Color().setHSL(h, s, l);
}

function isValidAnalysis(a: HarmonicAnalysis): boolean {
  if (!a || !a.spatialVector) return false;
  const [x, y, z] = a.spatialVector;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return false;
  if (!Number.isFinite(a.baseFrequency) || a.baseFrequency <= 0) return false;
  if (!Number.isFinite(a.harmonicDensity)) return false;
  if (!Number.isFinite(a.consonanceRatio)) return false;
  if (!a.mappedColor) return false;
  return true;
}

function computeEdgeWeight(a: HarmonicAnalysis, b: HarmonicAnalysis): number {
  const dx = a.spatialVector[0] - b.spatialVector[0];
  const dy = a.spatialVector[1] - b.spatialVector[1];
  const dz = a.spatialVector[2] - b.spatialVector[2];
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const modeSim = a.mode === b.mode ? 0.3 : 0;
  const keySim = a.key === b.key ? 0.2 : 0;
  return Math.max(0, 1 - dist * 2) + modeSim + keySim;
}

function spatialToScene(sv: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(
    sv[0] * 2.5,
    sv[1] * 2.5 - 0.5,
    sv[2] * 2.5 - 1
  );
}

function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
    );
  } catch (_) {
    return false;
  }
}

export function HarmonicManifold({ points, onPointClick, className, width, height }: HarmonicManifoldProps) {
  const webGLSupported = useRef(isWebGLAvailable());

  if (!webGLSupported.current) {
    return (
      <div className={`relative rounded-xl border border-amber-500/20 bg-[#0a0a1a] p-4 ${className || ""}`}>
        <div className="flex items-center gap-2 text-amber-400/60 text-sm">
          <span>Harmonic Manifold requires WebGL</span>
        </div>
      </div>
    );
  }

  return <ManifoldCanvas points={points} onPointClick={onPointClick} className={className} width={width} height={height} />;
}

function ManifoldCanvas({ points, onPointClick, className, width, height }: HarmonicManifoldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const nodesRef = useRef<ManifoldNode[]>([]);
  const edgesRef = useRef<ManifoldEdge[]>([]);
  const frameRef = useRef(0);
  const timeRef = useRef(0);
  const prevCountRef = useRef(0);
  const nodeGroupRef = useRef<THREE.Group | null>(null);
  const edgeGroupRef = useRef<THREE.Group | null>(null);
  const gridRef = useRef<THREE.Group | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const selectedIndexRef = useRef<number>(-1);
  const [selectedPoint, setSelectedPoint] = useState<SelectedPoint | null>(null);
  const [nodeCount, setNodeCount] = useState(0);
  const [edgeCount, setEdgeCount] = useState(0);

  const validPoints = points.filter(p => isValidAnalysis(p.analysis));

  const initScene = useCallback(() => {
    if (!containerRef.current) return;

    const w = width || containerRef.current.clientWidth;
    const h = height || containerRef.current.clientHeight || 300;

    const scene = new THREE.Scene();
    scene.background = BG_COLOR;
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
    camera.position.set(0, 1.5, CAMERA_DISTANCE);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: !isMobile(), alpha: false });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile() ? 1.5 : 2));
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    renderer.domElement.style.touchAction = "none";

    const ambient = new THREE.AmbientLight(0x404060, 0.6);
    scene.add(ambient);
    const point = new THREE.PointLight(0xffaa44, 1.2, 20);
    point.position.set(3, 4, 3);
    scene.add(point);

    const nodeGroup = new THREE.Group();
    scene.add(nodeGroup);
    nodeGroupRef.current = nodeGroup;

    const edgeGroup = new THREE.Group();
    scene.add(edgeGroup);
    edgeGroupRef.current = edgeGroup;

    const grid = buildGrid();
    scene.add(grid);
    gridRef.current = grid;
  }, [width, height]);

  const buildGrid = useCallback(() => {
    const group = new THREE.Group();
    const gridMat = new THREE.LineBasicMaterial({ color: GRID_COLOR, transparent: true, opacity: 0.3 });

    const size = 3;
    const divisions = 6;
    const step = (size * 2) / divisions;

    for (let i = 0; i <= divisions; i++) {
      const pos = -size + i * step;
      const xGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(pos, -1.5, -size),
        new THREE.Vector3(pos, -1.5, size),
      ]);
      group.add(new THREE.Line(xGeo, gridMat));
      const zGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-size, -1.5, pos),
        new THREE.Vector3(size, -1.5, pos),
      ]);
      group.add(new THREE.Line(zGeo, gridMat));
    }
    return group;
  }, []);

  const sphereSegments = isMobile() ? MOBILE_MAX_TRIANGLES : DESKTOP_MAX_TRIANGLES;

  const createNodeMesh = useCallback((node: ManifoldNode): THREE.Mesh => {
    const geo = new THREE.SphereGeometry(node.radius, sphereSegments, sphereSegments * 0.75);
    const mat = new THREE.MeshPhongMaterial({
      color: node.color,
      emissive: node.color.clone().multiplyScalar(0.4),
      transparent: true,
      opacity: 1.0,
      shininess: 60,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(node.position);
    mesh.userData = { nodeIndex: node.index, messageId: node.messageId };
    return mesh;
  }, [sphereSegments]);

  const createEdgeLine = useCallback((from: THREE.Vector3, to: THREE.Vector3, weight: number, fromColor: THREE.Color): THREE.Line => {
    const geo = new THREE.BufferGeometry().setFromPoints([from, to]);
    const opacity = Math.min(1, EDGE_OPACITY_BASE * weight);
    const mat = new THREE.LineBasicMaterial({
      color: fromColor.clone().multiplyScalar(0.7),
      transparent: true,
      opacity,
    });
    return new THREE.Line(geo, mat);
  }, []);

  const clearAll = useCallback(() => {
    for (const edge of edgesRef.current) {
      if (edge.line && edgeGroupRef.current) {
        edgeGroupRef.current.remove(edge.line);
        edge.line.geometry.dispose();
        (edge.line.material as THREE.Material).dispose();
      }
    }
    for (const node of nodesRef.current) {
      if (node.mesh && nodeGroupRef.current) {
        nodeGroupRef.current.remove(node.mesh);
        node.mesh.geometry.dispose();
        (node.mesh.material as THREE.Material).dispose();
      }
    }
    nodesRef.current = [];
    edgesRef.current = [];
    selectedIndexRef.current = -1;
    setSelectedPoint(null);
    setNodeCount(0);
    setEdgeCount(0);
  }, []);

  const syncNodes = useCallback(() => {
    if (!nodeGroupRef.current || !edgeGroupRef.current) return;

    const existing = nodesRef.current.length;
    const incoming = validPoints.length;

    if (incoming <= existing) return;

    const newPoints = validPoints.slice(existing);

    for (const pt of newPoints) {
      const position = spatialToScene(pt.analysis.spatialVector);
      const color = parseHSL(pt.analysis.mappedColor);
      const radius = NODE_BASE_RADIUS + pt.analysis.harmonicDensity * 0.04 + pt.analysis.consonanceRatio * 0.02;

      const node: ManifoldNode = {
        position,
        color,
        radius,
        age: 0,
        messageId: pt.messageId,
        index: pt.index,
        analysis: pt.analysis,
        mesh: null,
      };

      const mesh = createNodeMesh(node);
      node.mesh = mesh;
      nodeGroupRef.current!.add(mesh);
      nodesRef.current.push(node);
    }

    if (nodesRef.current.length > MAX_NODES) {
      const removeCount = nodesRef.current.length - MAX_NODES;
      for (let i = 0; i < removeCount; i++) {
        const old = nodesRef.current[i];
        if (old.mesh) {
          nodeGroupRef.current!.remove(old.mesh);
          old.mesh.geometry.dispose();
          (old.mesh.material as THREE.Material).dispose();
        }
      }
      nodesRef.current = nodesRef.current.slice(removeCount);
    }

    for (const edge of edgesRef.current) {
      if (edge.line) {
        edgeGroupRef.current!.remove(edge.line);
        edge.line.geometry.dispose();
        (edge.line.material as THREE.Material).dispose();
      }
    }
    edgesRef.current = [];

    const nodes = nodesRef.current;
    for (let i = 1; i < nodes.length; i++) {
      const prev = nodes[i - 1];
      const curr = nodes[i];
      const weight = computeEdgeWeight(prev.analysis, curr.analysis);

      if (weight > 0.1) {
        const line = createEdgeLine(prev.position, curr.position, weight, curr.color);
        edgeGroupRef.current!.add(line);
        edgesRef.current.push({ from: i - 1, to: i, weight, line });
      }

      if (i >= 2) {
        const prev2 = nodes[i - 2];
        const crossWeight = computeEdgeWeight(prev2.analysis, curr.analysis);
        if (crossWeight > 0.5) {
          const crossLine = createEdgeLine(prev2.position, curr.position, crossWeight * 0.5, curr.color);
          edgeGroupRef.current!.add(crossLine);
          edgesRef.current.push({ from: i - 2, to: i, weight: crossWeight * 0.5, line: crossLine });
        }
      }
    }

    setNodeCount(nodesRef.current.length);
    setEdgeCount(edgesRef.current.length);
  }, [validPoints, createNodeMesh, createEdgeLine]);

  const handleClick = useCallback((event: MouseEvent | TouchEvent) => {
    if (!containerRef.current || !cameraRef.current || !nodeGroupRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    let clientX: number, clientY: number;

    if ("changedTouches" in event && event.changedTouches.length > 0) {
      clientX = event.changedTouches[0].clientX;
      clientY = event.changedTouches[0].clientY;
    } else if ("clientX" in event) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else {
      return;
    }

    mouseRef.current.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    const meshes = nodesRef.current
      .map(n => n.mesh)
      .filter((m): m is THREE.Mesh => !!m);

    const intersects = raycasterRef.current.intersectObjects(meshes);

    if (intersects.length > 0) {
      const hit = intersects[0].object;
      const nodeIdx = nodesRef.current.findIndex(n => n.mesh === hit);

      if (nodeIdx >= 0) {
        const node = nodesRef.current[nodeIdx];

        for (const n of nodesRef.current) {
          if (n.mesh) {
            const mat = n.mesh.material as THREE.MeshPhongMaterial;
            mat.emissive.copy(n.color).multiplyScalar(0.4);
          }
        }

        const mat = node.mesh!.material as THREE.MeshPhongMaterial;
        mat.emissive.set(0xffffff).multiplyScalar(HIGHLIGHT_EMISSIVE);

        selectedIndexRef.current = nodeIdx;

        const [x, y, z] = node.analysis.spatialVector;
        setSelectedPoint({
          messageId: node.messageId,
          index: node.index,
          x, y, z,
          key: node.analysis.key,
          scaleDegree: node.analysis.scaleDegree,
          mode: node.analysis.mode,
          centroid: node.analysis.spectralCentroid,
          density: node.analysis.harmonicDensity,
          consonanceRatio: node.analysis.consonanceRatio,
          mappedColor: node.analysis.mappedColor,
          baseFrequency: node.analysis.baseFrequency,
        });

        if (onPointClick) {
          onPointClick(node.messageId);
        }
      }
    } else {
      for (const n of nodesRef.current) {
        if (n.mesh) {
          const mat = n.mesh.material as THREE.MeshPhongMaterial;
          mat.emissive.copy(n.color).multiplyScalar(0.4);
        }
      }
      selectedIndexRef.current = -1;
      setSelectedPoint(null);
    }
  }, [onPointClick]);

  const animate = useCallback(() => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

    timeRef.current += 0.016;
    const t = timeRef.current;

    const camAngle = t * CAMERA_ORBIT_SPEED;
    const camX = Math.sin(camAngle) * CAMERA_DISTANCE;
    const camZ = Math.cos(camAngle) * CAMERA_DISTANCE;
    cameraRef.current.position.set(camX, 1.2 + Math.sin(t * 0.15) * 0.3, camZ);
    cameraRef.current.lookAt(0, 0, 0);

    for (let i = 0; i < nodesRef.current.length; i++) {
      const node = nodesRef.current[i];
      node.age += TIME_DECAY_RATE;
      if (node.mesh) {
        const decay = Math.max(MIN_OPACITY, 1 - node.age);
        const mat = node.mesh.material as THREE.MeshPhongMaterial;
        mat.opacity = decay;

        if (i === selectedIndexRef.current) {
          mat.opacity = Math.max(0.8, decay);
        }

        const breathe = 1 + Math.sin(t * 2 + node.position.x * 3) * 0.05;
        node.mesh.scale.setScalar(i === selectedIndexRef.current ? breathe * 1.3 : breathe);
      }
    }

    for (const edge of edgesRef.current) {
      if (edge.line) {
        const fromAge = nodesRef.current[edge.from]?.age ?? 0;
        const toAge = nodesRef.current[edge.to]?.age ?? 0;
        const avgDecay = Math.max(MIN_OPACITY * 0.5, 1 - (fromAge + toAge) / 2);
        const mat = edge.line.material as THREE.LineBasicMaterial;
        mat.opacity = EDGE_OPACITY_BASE * edge.weight * avgDecay;
      }
    }

    rendererRef.current.render(sceneRef.current, cameraRef.current);
    frameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    initScene();
    frameRef.current = requestAnimationFrame(animate);

    const canvas = rendererRef.current?.domElement;
    if (canvas) {
      canvas.addEventListener("click", handleClick);
      canvas.addEventListener("touchend", handleClick, { passive: true });
    }

    return () => {
      cancelAnimationFrame(frameRef.current);
      if (canvas) {
        canvas.removeEventListener("click", handleClick);
        canvas.removeEventListener("touchend", handleClick);
      }
      for (const edge of edgesRef.current) {
        if (edge.line) {
          edge.line.geometry.dispose();
          (edge.line.material as THREE.Material).dispose();
        }
      }
      for (const node of nodesRef.current) {
        if (node.mesh) {
          node.mesh.geometry.dispose();
          (node.mesh.material as THREE.Material).dispose();
        }
      }
      if (gridRef.current) {
        gridRef.current.traverse((obj) => {
          if (obj instanceof THREE.Line) {
            obj.geometry.dispose();
            (obj.material as THREE.Material).dispose();
          }
        });
      }
      if (rendererRef.current && containerRef.current) {
        try { containerRef.current.removeChild(rendererRef.current.domElement); } catch (_) { /* DOM node already removed */ }
        rendererRef.current.dispose();
      }
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      nodeGroupRef.current = null;
      edgeGroupRef.current = null;
      gridRef.current = null;
      nodesRef.current = [];
      edgesRef.current = [];
    };
  }, [initScene, animate, handleClick]);

  useEffect(() => {
    if (validPoints.length < prevCountRef.current) {
      clearAll();
      prevCountRef.current = 0;
    }
    if (validPoints.length !== prevCountRef.current) {
      prevCountRef.current = validPoints.length;
      syncNodes();
    }
  }, [validPoints, syncNodes, clearAll]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      const w = width || containerRef.current.clientWidth;
      const h = height || containerRef.current.clientHeight || 300;
      rendererRef.current.setSize(w, h);
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [width, height]);

  return (
    <div
      ref={containerRef}
      className={`relative rounded-xl overflow-hidden border border-amber-500/10 ${className || ""}`}
      style={{ width: width || "100%", height: height || 300, touchAction: "none" }}
      data-testid="harmonic-manifold"
    >
      {validPoints.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-amber-400/30 text-sm pointer-events-none z-10">
          Harmonic manifold builds as conversation flows
        </div>
      )}
      {nodeCount > 0 && (
        <div className="absolute top-2 left-2 z-10 pointer-events-none">
          <div className="text-[10px] font-mono text-amber-400/50 bg-black/40 px-1.5 py-0.5 rounded">
            {nodeCount} node{nodeCount !== 1 ? "s" : ""} · {edgeCount} edge{edgeCount !== 1 ? "s" : ""}
          </div>
        </div>
      )}
      {selectedPoint && (
        <div
          className="absolute bottom-2 left-2 right-2 z-20 bg-black/80 backdrop-blur-sm rounded-lg border border-amber-500/20 p-2 text-[10px] font-mono text-amber-100/80"
          data-testid="manifold-debug-overlay"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[120px]">
              <div className="text-amber-400/60 mb-0.5">Point #{selectedPoint.index + 1}</div>
              <div>
                <span className="text-amber-300">{selectedPoint.key}{selectedPoint.scaleDegree}</span>
                <span className="text-amber-400/40 mx-1">·</span>
                <span>{selectedPoint.mode}</span>
                <span className="text-amber-400/40 mx-1">·</span>
                <span>{selectedPoint.baseFrequency.toFixed(1)}Hz</span>
              </div>
            </div>
            <div className="flex-1 min-w-[120px]">
              <div className="text-amber-400/60 mb-0.5">Spatial</div>
              <div>
                x={selectedPoint.x.toFixed(3)} y={selectedPoint.y.toFixed(3)} z={selectedPoint.z.toFixed(3)}
              </div>
            </div>
            <div className="flex-1 min-w-[120px]">
              <div className="text-amber-400/60 mb-0.5">Metrics</div>
              <div>
                centroid={selectedPoint.centroid.toFixed(0)}Hz
                <span className="text-amber-400/40 mx-1">·</span>
                density={selectedPoint.density.toFixed(2)}
                <span className="text-amber-400/40 mx-1">·</span>
                consonance={selectedPoint.consonanceRatio.toFixed(2)}
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-full border border-white/20"
                style={{ background: selectedPoint.mappedColor }}
              />
              <span className="text-[9px] text-amber-400/40">{selectedPoint.messageId.slice(0, 8)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
